import React, { useEffect } from "react";
import { Button, FormInstance, Modal, notification } from "antd";
import Icon from "@mdi/react";
import { mdiMinus, mdiPlus } from "@mdi/js";
import { validateBooking } from "../../utils/helpers";
import { BookingInput } from "../../interfaces/booking.interface";

interface TravelersProps {
  travelerCounts: Record<string, number>;
  setTravelerCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  selectedTravelers: string[];
  setSelectedTravelers: React.Dispatch<React.SetStateAction<string[]>>;
  paxMultiplier: number;
  maxBookingSeats: number;
  isCruise: boolean;
  cabin: number;
  setCabin: React.Dispatch<React.SetStateAction<number>>;
  pricingCategories: Record<string, string>;
  bookingPaxMultiplier: number;
  b2c2b: boolean;
  bookingForm?: FormInstance;
  bookingInputParams: BookingInput;
}

const Travelers: React.FC<TravelersProps> = ({
  travelerCounts,
  setTravelerCounts,
  selectedTravelers,
  setSelectedTravelers,
  paxMultiplier,
  maxBookingSeats,
  isCruise,
  cabin,
  setCabin,
  pricingCategories,
  bookingPaxMultiplier,
  b2c2b,
  bookingForm,
  bookingInputParams
}) => {
  const minPaxPerCabin = bookingPaxMultiplier;
  const currentTotalPax = selectedTravelers.length;
  const allowedCabin = Math.floor(maxBookingSeats / bookingPaxMultiplier);
  const requiredTotalPax = cabin * minPaxPerCabin;
  const [errorMessage, setErrorMessage] = React.useState("");

  const handleCabinDecrease = () => {
    if (currentTotalPax > (cabin - 1) * minPaxPerCabin) {
      Modal.warning({
        title: "Unable to reduce the Cabin",
        content: "Kindly reduce the travelers in order to reduce the Cabin.",
        okText: "OK",
      });
      return;
    }
    setCabin((prevCabin) => Math.max(1, prevCabin - 1));
  };

  const handleCabinIncrease = () => {
    setCabin((prevCabin) => Math.min(prevCabin + 1, allowedCabin));
  };

  const handleAddTraveler = (categoryKey: string) => {
    const count = travelerCounts[categoryKey] || 0;

    // Check if adding would exceed max booking seats
    if (selectedTravelers.length + paxMultiplier > maxBookingSeats) {
      notification.warning({
        message: "Maximum booking seats reached",
      });
      return;
    }

    setTravelerCounts((prevCounts) => ({
      ...prevCounts,
      [categoryKey]: count + paxMultiplier,
    }));

    setSelectedTravelers((prevTravelers) => {
      const newTravelers = Array(paxMultiplier).fill(categoryKey);
      return [...prevTravelers, ...newTravelers];
    });
  };

  const handleRemoveTraveler = (categoryKey: string) => {
    const count = travelerCounts[categoryKey] || 0;

    if (count > 0) {
      setTravelerCounts((prevCounts) => ({
        ...prevCounts,
        [categoryKey]: count - paxMultiplier,
      }));

      setSelectedTravelers((prevTravelers) => {
        const updatedTravelers = [...prevTravelers];
        let removed = 0;

        if(b2c2b){
          const passengers = bookingForm?.getFieldValue("passengers") || [];
          // Remove `paxMultiplier` entries for the category
          for (let i = updatedTravelers.length - 1; i >= 0 && removed < paxMultiplier; i--) {
            if (updatedTravelers[i] === categoryKey) {
              updatedTravelers.splice(i, 1);
              passengers.splice(i, 1);
              removed++;
            }
          }

          bookingForm?.setFieldsValue({
            ["passengers"]: [...bookingForm?.getFieldValue("passengers")],
          });
        }

        return updatedTravelers;
      });
    }
  };

  useEffect(() => {
    const validBooking = validateBooking(bookingInputParams, isCruise);
    if(validBooking.isValid == false){
      setErrorMessage(validBooking.message);

      // // Clear error message after 10 seconds
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);

      return () => clearTimeout(timer);
    }
    setErrorMessage("");
  }, [bookingInputParams])

  return (
    <div className="travelers-content">
      {isCruise && (
        <div className="traveler-category highlight">
          <div>
            <span className="traveler-category-label">Cabin</span>
            <small>
              <i>Each cabin {minPaxPerCabin} pax</i>
            </small>
          </div>
          <div className="traveler-actions">
            <Button
              shape="circle"
              icon={<Icon path={mdiMinus} size="20px" />}
              type="primary"
              onClick={handleCabinDecrease}
              disabled={cabin === 1}
            />
            {cabin}
            <Button
              shape="circle"
              icon={<Icon path={mdiPlus} size="20px" />}
              type="primary"
              onClick={handleCabinIncrease}
              disabled={cabin === allowedCabin}
            />
          </div>
        </div>
      )}

      {Object.entries(pricingCategories).map(([categoryKey, categoryLabel], index) => {
        const count = travelerCounts[categoryKey] || 0;
        const disableAdd = currentTotalPax === maxBookingSeats || (isCruise && requiredTotalPax === currentTotalPax);

        return (
          <div key={index} className="traveler-category">
            <div>
              <span className="traveler-category-label">{String(categoryLabel)}</span>
              <small>
                <i>{categoryKey.includes("child") ? "2-11 years old" : "12 years & above"}</i>
              </small>
            </div>
            <div className="traveler-actions">
              <Button
                shape="circle"
                icon={<Icon path={mdiMinus} size="20px" />}
                type="primary"
                onClick={() => handleRemoveTraveler(categoryKey)}
                disabled={count === 0}
              />
              {count}
              <Button
                shape="circle"
                icon={<Icon path={mdiPlus} size="20px" />}
                type="primary"
                onClick={() => handleAddTraveler(categoryKey)}
                disabled={disableAdd}
              />
            </div>
          </div>
        );
      })}

      {(!isCruise && !!errorMessage) && (
        <div className="booking-note">
          <small>
            <i> {errorMessage} </i>
          </small>
        </div>
      )}
    </div>
  );
};

export default Travelers;